import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import usePoll from "../../hooks/usePoll";
import { useSocketEvent } from "../../hooks/useSocketEvent";
import { fetchWithAuth } from "../../utils/api";
import { TagIcon } from "../../utils/tagIcons";
import { fmtSpan, secondsSince } from "../osr/osrShared";
import { useNow } from "../osr/useCountdown";
import "../osr/OsrAlerts.css";
import "./BookingAlerts.css";

const POLL_MS = 60000;
const MAX_CARDS = 2;
const PENDING_PATH = "/app/sales-intake/pending";
const SOON_MS = 400;
// Sales Intake saves a booking's form as someone types ("edited"): one read after the burst, not one per save.
const AFTER_TYPING_MS = 5000;
const DAY = new Intl.DateTimeFormat("en-US", { timeZone: "UTC", weekday: "short", month: "short", day: "numeric" });

/** "Mobile visit · Fri, Oct 23, morning" / "Shop appointment · today". */
export function visitWords(b) {
  const type = b.appointment_type === "shop" ? "Shop appointment" : "Mobile visit";
  const day = b.same_day ? "today"
    : /^\d{4}-\d{2}-\d{2}$/.test(b.requested_date || "") ? DAY.format(new Date(`${b.requested_date}T12:00:00Z`)) : null;
  const time = b.requested_time === "morning" || b.requested_time === "afternoon" ? b.requested_time : null;
  return [type, [day, time].filter(Boolean).join(", ")].filter(Boolean).join(" · ");
}

/**
 * The bookings waiting in Pending Review, as cards at the bottom right of every page: each online booking and each
 * visit Hank booked, until someone sends it to Sales Sheets or archives it (Allen, Oct 19 2026: "kinda like an osr that
 * gets shown to admins and sales managers until its been reviewed and submitted then it should go away"). Only one with
 * a card on file: an NS booking goes to OSR instead and gets its card here once that is in (Oct 28 2026). No close
 * button: the card leaves when the booking does. Review opens it on Pending Review.
 *
 * Who sees them: calls.booking_alerts (admins and sales managers by default). What waits is the server's
 * (GET /admin/calls/booking-alerts, chariot-backend routes/admin/callBookingAlerts.js): urgent first, then the longest
 * waiting. Off the Pending Review tab itself, where the same bookings are pinned at the top and the open booking's
 * Submit button sits in this corner.
 *
 * Rendered inside the sitewide alert stack (SiteAlertCards) with the OSR lead cards, in the OSR card's look
 * (components/osr/OsrAlerts.css).
 *
 *   POLL BUDGET  once a minute per open tab, only for people with the permission, paused while the tab is hidden
 *   (usePoll). A call notice (call:updated) about a new row or one of the bookings shown prompts a read in between.
 */
export default function BookingAlerts() {
  const { can, user } = useAuth();
  const allowed = !!user && can("calls.booking_alerts");
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const shown = useRef(new Set()); // the ids listed, for the call notices that concern them
  const timer = useRef(null);
  const due = useRef(0);

  const load = useCallback(async () => {
    try {
      const res = await fetchWithAuth("/admin/calls/booking-alerts", { skipSaveIndicator: true });
      if (!res.ok) return false;
      const json = await res.json();
      if (!json.success) return false;
      shown.current = new Set((json.bookings || []).map((b) => String(b.id)));
      setData(json);
      return true;
    } catch {
      return false; // usePoll backs off; the last cards stay up
    }
  }, []);
  const readNow = usePoll(load, POLL_MS, { enabled: allowed });

  const onCallUpdated = useCallback((msg) => {
    if (!allowed) return;
    const ids = Array.isArray(msg?.ids) ? msg.ids : msg?.id ? [msg.id] : [];
    if (msg?.action !== "created" && !ids.some((id) => shown.current.has(String(id)))) return;
    const at = Date.now() + (msg?.action === "edited" ? AFTER_TYPING_MS : SOON_MS);
    if (timer.current && due.current <= at) return; // a read is already coming sooner
    clearTimeout(timer.current);
    due.current = at;
    timer.current = setTimeout(() => { timer.current = null; readNow(); }, at - Date.now());
  }, [allowed, readNow]);
  useSocketEvent("call:updated", onCallUpdated);
  useEffect(() => () => clearTimeout(timer.current), []);

  const bookings = allowed ? data?.bookings || [] : [];
  const now = useNow(30000, bookings.length > 0);
  const onPendingReview = /^\/app\/sales-intake(\/pending)?\/?$/.test(location.pathname);
  if (!bookings.length || onPendingReview) return null;

  const cards = bookings.slice(0, MAX_CARDS);
  const more = Math.max(0, (data.count || bookings.length) - cards.length);
  return (
    <>
      {cards.map((b) => {
        const online = b.kind === "online";
        return (
          <div key={b.id} className="osr-alert booking-alert" data-kind={b.kind} data-level={b.urgent ? "high" : undefined}>
            <span className="osr-alert-icon" aria-hidden="true">
              <TagIcon name={online ? "computer" : "phone"} size={17} />
            </span>
            <div className="osr-alert-body">
              <div className="osr-alert-title">{online ? "Online booking to review" : "Hank booked a visit"}</div>
              <div className="osr-alert-name">
                {b.name || "New customer"}
                {b.urgent && <span className="lc2-urgent-pill">URGENT</span>}
              </div>
              <div className="osr-alert-meta">
                <span>{visitWords(b)}{b.market ? ` · ${b.market}` : ""}</span>
                <span>Waiting {fmtSpan(secondsSince(b.booked_at, now))} · stays until it is sent or archived</span>
              </div>
              <div className="osr-alert-actions">
                <button type="button" className="osr-alert-btn osr-alert-btn-main" onClick={() => navigate(`${PENDING_PATH}?call=${encodeURIComponent(b.id)}`)}>
                  Review
                </button>
              </div>
            </div>
          </div>
        );
      })}
      {more > 0 && (
        <button type="button" className="osr-alert osr-alert-more" onClick={() => navigate(PENDING_PATH)}>
          +{more} more booking{more === 1 ? "" : "s"} waiting · Open Pending Review
        </button>
      )}
    </>
  );
}
